import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Breadcrumb, Button, Pagination, Select, Spin,
} from 'antd';
import { NavLink } from 'react-router-dom';
import SearchCard from './Card';
import { useArticleContext } from '../../Common/Header/context';

import styles from './index.module.scss';
import SelectedFilters from '../../Common/Header/Filter/SelectedFilters';
import NoData from '../../Common/NoData';

export enum SortType {
  Newest = 'newest',
  // Oldest = 'oldest',
  TitleAZ = 'az',
  MostViewed = 'viewed',
  MostHelpful = 'helpful',
  TitleZA = 'za',
}

const sortOptions = [
  { label: 'Most relevant', value: SortType.Newest },
  // { label: 'Oldest', value: SortType.Oldest },
  { label: 'Most viewed', value: SortType.MostViewed },
  { label: 'Most helpful', value: SortType.MostHelpful },
  { label: 'A-Z', value: SortType.TitleAZ },
  { label: 'Z–A', value: SortType.TitleZA },
];

export const sortMap: Record<SortType, { orderByColumn: string; orderBy: 'ASC' | 'DESC' }> = {
  [SortType.Newest]: { orderByColumn: 'createdAt', orderBy: 'DESC' },
  // [SortType.Oldest]: { orderByColumn: 'createdAt', orderBy: 'ASC' },
  [SortType.MostViewed]: { orderByColumn: 'views', orderBy: 'DESC' },
  [SortType.MostHelpful]: { orderByColumn: 'helpful', orderBy: 'DESC' },
  [SortType.TitleAZ]: { orderByColumn: 'title', orderBy: 'ASC' },
  [SortType.TitleZA]: { orderByColumn: 'title', orderBy: 'DESC' },
};

export default function SearchResultsContent(): React.ReactNode | null {
  // const [sortValue, setSortValue] = useState<SortType>(SortType.TitleAZ);
  const {
    fetch, data, meta, filters, setSearch, setFilters, loading, sortValue = SortType.TitleAZ,
    setSortValue,
  } = useArticleContext();
  const { id, name } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('title');
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const refetch = () => {
    if (!fetch) return;

    const sortParams = sortMap[sortValue];

    fetch({
      pageSize: '10',
      title: query || '',
      page: page.toString(),
      categoryId: filters?.categoryId,
      subCategoryId: filters?.subCategoryId,
      types: filters?.types,
      tags: filters?.tags,
      status: 'published',
      orderByColumn: sortParams.orderByColumn,
      orderBy: sortParams.orderBy,
      productId: id,
    });
  };

  useEffect(() => {
    if (fetch) {
      const sortParams = sortMap[sortValue];

      setSearch?.(query || '');

      fetch({
        pageSize: '10',
        title: query || '',
        page: page.toString(),
        categoryId: filters?.categoryId,
        subCategoryId: filters?.subCategoryId,
        types: filters?.types,
        tags: filters?.tags,
        status: 'published',
        orderByColumn: sortParams.orderByColumn,
        orderBy: sortParams.orderBy,
        productId: id,
      });

      wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [query, page, filters, sortValue]);
  useEffect(() => {
    const urlCategoryIds = searchParams.get('categoryId')?.split(',').filter(Boolean) ?? [];
    const urlSubCategoryIds = searchParams.get('subCategoryId')?.split(',').filter(Boolean) ?? [];
    const urlTypes = searchParams.get('type')?.split(',').filter(Boolean) ?? [];
    const urlTags = searchParams.get('tags')?.split(',').filter(Boolean) ?? [];

    setFilters?.({
      categoryId: urlCategoryIds,
      subCategoryId: urlSubCategoryIds,
      types: urlTypes,
      tags: urlTags,
    });
  }, [searchParams]);
  // eslint-disable-next-line @typescript-eslint/no-shadow, unicorn/consistent-function-scoping
  const onChange = (newPage: number) => {
    const currentTitle = searchParams.get('title') ?? '';

    setSearchParams({
      title: currentTitle,
      page: newPage.toString(),

    });
  };

  return (
    <div className="wrapper" ref={wrapperRef}>
      <div className={styles.content}>
        <div className={styles.breadcrumb}>
          <Breadcrumb
            items={[
              {
                title: <NavLink to="/" className={styles.breadcrumbLink}>Help portal</NavLink>,
              },
              {
                title: 'Search results',
              },
            ]}
          />
        </div>
        {(filters?.categoryId?.length
          || filters?.subCategoryId?.length
          || filters?.types?.length
          || filters?.tags?.length)
          ? (
            <div className={styles.selectedFiltersHeader}>
              <SelectedFilters />
            </div>
          ) : null}
        <div className={styles.sorting}>
          <div className={styles.counter}>
            <span>
              {meta?.itemCount ?? 0}
              {' '}
              matches
            </span>
          </div>
          <div className={styles.counter}>
            <span style={{ marginRight: 12 }}>Sort by:</span>
            <Select
              value={sortValue}
              onChange={(val) => setSortValue?.(val as SortType)}
              style={{ width: 220, borderRadius: 6, height: 44 }}
              className="custom-select"
              dropdownStyle={{ borderRadius: 6 }}
              dropdownClassName="custom-dropdown"
            >
              {sortOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        {data?.length
          ? data.map((item) => (
            <div key={item.id}>
              <SearchCard data={item} refetch={refetch} />
            </div>
          ))
          : <NoData isSearchPage />}
      </div>
      <div className="customPagination">
        <div style={{ textAlign: 'center' }}>
          <Pagination
            showSizeChanger={false}
            total={meta?.itemCount ?? 0}
            pageSize={meta?.pageSize ?? 10}
            current={page}
            onChange={onChange}
          />
          <div style={{ marginTop: 8, color: '#1F3847AD' }}>
            {meta
              ? `${(page - 1) * meta.pageSize + 1}
              –${Math.min(page * meta.pageSize, meta.itemCount)} of ${meta.itemCount} results`
              : '0 results'}
          </div>
        </div>
      </div>
      {loading ? (
        <div className="spin">
          <Spin />
        </div>
      ) : null}
    </div>
  );
}
